/**
 * The documents of a quote (rule of 03/10/2026, «Documenti» of 04/10/2026):
 * the «Offerta economica» for the client, revenues and conditions only, and
 * the internal «Preventivo interno», with costs, margins, checks and approvals.
 * The page that prints the PDF and the API that writes the Word file read the
 * same model, so the two formats always say the same things.
 */
import { z } from 'zod';
import { formatMoney, formatPercent, formatQuantity, fromCents, toCents, } from './money.js';
import { CHECK_OUTCOME_LABELS } from './offer-checks.js';
import { offerLabel, phaseOf } from './quotes.js';
export const quoteDocumentKindSchema = z.enum(['offerta', 'preventivo']);
export const QUOTE_DOCUMENT_LABELS = {
    offerta: 'Offerta economica',
    preventivo: 'Preventivo interno',
};
/** K-Rev as its letterhead says it. */
export const COMPANY = {
    name: 'K-Rev S.r.l.',
    footer: [
        'K-Rev S.r.l., c/o ComoNext, Via Cavour 2, 22074 Lomazzo (CO)',
        'info@k-rev.com',
        'www.k-rev.com',
        'C.F. e P.IVA 03715620138',
    ],
};
/** The orange of K-Rev (brand guide): logo, platform, total. */
export const BRAND_ACCENT = '#fa440f';
/**
 * The colour of each business area in the documents (brand guide, 04/10/2026):
 * by the first-level type of the lines. The only place to change it until the
 * setup page; a section mixing areas, and «Altro», stay neutral.
 */
export const AREA_COLOURS = {
    FOR: '#f2a749', // Formazione: ocra
    CON: '#a68278', // Consulenza: marrone rosato
    LIC: BRAND_ACCENT, // Piattaforma K-Rev 3.0: arancio
    SVI: BRAND_ACCENT, // Sviluppo della piattaforma: arancio
};
export const NEUTRAL_AREA = '#d9cdc9';
/** The colour of a group of lines: the one of their area, neutral when they mix areas. */
export function areaColour(typeCodes) {
    const colours = new Set(typeCodes.map((code) => AREA_COLOURS[code] ?? NEUTRAL_AREA));
    return colours.size === 1 ? [...colours][0] : NEUTRAL_AREA;
}
const MONTHS = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
/** «2026-10-02» → «2 ottobre 2026», «1° novembre 2026»: a document to sign, not a register. */
export function longDate(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    return `${day === 1 ? '1°' : day} ${MONTHS[month - 1]} ${year}`;
}
/** The instalments of the payment plan, one per line: «Alla firma: 30% · 6.951,00 € · a ricevimento fattura». */
export function installmentLines(quote, labels) {
    return quote.installments.map((item) => [
        `${item.description}: ${item.percent !== null ? `${Number(item.percent).toLocaleString('it-IT')}% · ` : ''}${formatMoney(item.computed)}`,
        item.termsCode ? label(labels.paymentTerms, item.termsCode) : null,
        item.notes,
    ]
        .filter(Boolean)
        .join(' · '));
}
/** «All’avvio» → «all’avvio» inside a sentence; «SAL intermedio» stays. */
function sentenceCase(text) {
    return /^\p{Lu}(?!\p{Lu})/u.test(text) ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}
/**
 * How the client pays, as the offer says it (2.58): one line per instalment of
 * the payment plan, «75% all’avvio dei lavori, a ricevimento fattura»; without
 * a plan the payment terms. The billing notes written by hand follow.
 */
export function paymentText(quote, labels) {
    const plan = quote.installments.map((item) => {
        const share = item.percent !== null
            ? `${Number(item.percent).toLocaleString('it-IT')}%`
            : formatMoney(item.computed);
        const terms = item.termsCode ?? quote.paymentTermsCode;
        return [
            `${share} ${sentenceCase(item.description)}`,
            terms ? sentenceCase(label(labels.paymentTerms, terms)) : null,
            item.notes,
        ]
            .filter(Boolean)
            .join(', ');
    });
    const lines = plan.length > 0
        ? plan
        : quote.paymentTermsCode
            ? [label(labels.paymentTerms, quote.paymentTermsCode)]
            : [];
    if (quote.billingNotes)
        lines.push(quote.billingNotes);
    return lines.length > 0 ? lines.join('\n') : null;
}
/** The references of the client in K-Rev (2.58), in this order; who is not chosen is left out. */
export function offerContacts(quote) {
    return [
        { role: 'Project management', person: quote.projectManager },
        { role: 'Commerciale', person: quote.owner },
        { role: 'Amministrazione', person: quote.adminContact },
    ].flatMap(({ role, person }) => person ? [{ role, name: person.name, email: person.email }] : []);
}
/** «2026-10-02» → «02/10/2026». */
export function shortDate(iso) {
    const [year, month, day] = iso.split('-');
    return `${day}/${month}/${year}`;
}
/** An instant as people in Italy read it, wherever the server runs: «04/10/26, 16:37». */
export function italianTimestamp(value) {
    return new Date(value).toLocaleString('it-IT', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Rome',
    });
}
function label(entries, code) {
    return entries.find((entry) => entry.code === code)?.description ?? code;
}
/**
 * Why the document cannot be made for this quote; null when it can. The offer
 * exists from the approval; the internal document needs «Costi e margini».
 */
export function quoteDocumentBlocked(quote, kind) {
    if (kind === 'offerta' && phaseOf(quote.state) === 'preventivo') {
        return 'L’offerta economica esiste dopo l’approvazione: il preventivo è ancora per uso interno.';
    }
    if (kind === 'preventivo' && !quote.costs) {
        return 'Il preventivo interno contiene costi e margini: serve «Costi e margini».';
    }
    return null;
}
/** «OFF-2026-0001 v2 - Offerta economica - Cliente», without the extension. */
export function quoteDocumentFileName(quote, kind) {
    const name = quote.number ? offerLabel(quote.number, quote.version) : 'Preventivo';
    const client = quote.clientName ?? quote.referenceName ?? '';
    // Characters Windows refuses in a file name.
    return `${name} - ${QUOTE_DOCUMENT_LABELS[kind]}${client ? ` - ${client}` : ''}`
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * The lines as the client reads them (3.11): a line to sum above, the setup
 * avanzato, goes into the one before it, amounts summed, discounts included.
 */
export function offerLines(lines) {
    const result = [];
    for (const line of lines) {
        const above = result.at(-1);
        if (line.mergeUp && above) {
            const sum = (a, b) => fromCents(toCents(a) + toCents(b));
            result[result.length - 1] = {
                ...above,
                gross: sum(above.gross, line.gross),
                net: sum(above.net, line.net),
                sold: sum(above.sold, line.sold),
                // A price of the list no longer says anything of the sum.
                listPrice: null,
            };
        }
        else {
            result.push(line);
        }
    }
    return result;
}
/** The lines in the order of the body, each section with its lines. */
export function lineGroups(quote) {
    const result = [];
    for (const line of quote.lines) {
        const last = result[result.length - 1];
        if (last && (last.section?.id ?? null) === line.sectionId)
            last.lines.push(line);
        else
            result.push({
                section: quote.sections.find((section) => section.id === line.sectionId) ?? null,
                lines: [line],
            });
    }
    return result;
}
function referenceOf(quote) {
    return quote.number
        ? {
            title: `Offerta ${quote.number}`,
            detail: `versione ${quote.version ?? 1} del ${longDate(quote.date)}`,
        }
        : { title: 'Preventivo', detail: `del ${longDate(quote.date)}` };
}
/** «1,5 ore», or «3 edizioni × 1,5 ore» for a line repeated (2.49). */
export function quantityText(line, labels, lowerCase = false) {
    const unit = label(labels.units, line.unitCode);
    const text = `${formatQuantity(line.quantity)} ${lowerCase ? unit.toLowerCase() : unit}`;
    return line.editions > 1 ? `${line.editions} edizioni × ${text}` : text;
}
function recurrenceOf(line, labels) {
    return line.recurrence ?? quantityText(line, labels, true);
}
/** «Offerta economica», model «Semplice» (revised 04/10/2026). */
export function offerDocumentModel(quote, labels) {
    const clientName = quote.snapshot?.name ?? quote.clientName ?? '';
    const period = quote.startDate && quote.endDate
        ? `dal ${longDate(quote.startDate)} al ${longDate(quote.endDate)}`
        : quote.startDate
            ? `dal ${longDate(quote.startDate)}`
            : quote.endDate
                ? `fino al ${longDate(quote.endDate)}`
                : null;
    const terms = [];
    const payment = paymentText(quote, labels);
    if (payment)
        terms.push({ label: 'Pagamento', value: payment });
    if (period)
        terms.push({ label: 'Durata', value: period });
    terms.push({
        label: 'Validità',
        value: `${quote.validityDays} giorni${quote.validUntil ? `, fino al ${longDate(quote.validUntil)}` : ''}`,
    });
    const discount = Number(quote.totals.documentDiscount);
    return {
        reference: referenceOf(quote),
        title: QUOTE_DOCUMENT_LABELS.offerta,
        subtitle: quote.referenceName ? `Allegato all’offerta «${quote.referenceName}»` : null,
        client: {
            name: clientName,
            address: quote.snapshot?.address ?? null,
            vatNumber: quote.snapshot?.vatNumber ?? null,
        },
        groups: lineGroups(quote).map((group) => ({
            name: group.section?.name ?? null,
            subtotal: group.section ? formatMoney(group.section.net) : null,
            colour: areaColour(group.lines.map((line) => line.typeCode)),
            lines: offerLines(group.lines).map((line) => ({
                what: line.label,
                when: recurrenceOf(line, labels),
                amount: formatMoney(line.net),
            })),
        })),
        discount: discount > 0 ? `−${formatMoney(quote.totals.documentDiscount)}` : null,
        total: formatMoney(quote.totals.net),
        totalNote: 'IVA esclusa; spese di trasferta escluse',
        terms,
        clientNotes: quote.clientNotes,
        contacts: offerContacts(quote),
        signatures: [
            { title: `Per ${COMPANY.name}`, note: 'Il legale rappresentante' },
            { title: `Per ${clientName}, per accettazione`, note: 'Timbro e firma' },
        ],
        footer: COMPANY.footer,
    };
}
/** The internal document of the preventivo: revenues, costs, margins, checks, approvals. */
export function preventivoDocumentModel(quote, labels, printedAt) {
    const costs = quote.costs;
    const costOf = (lineId) => costs.lines.find((line) => line.lineId === lineId);
    const percent = (value) => value === null || value === undefined ? '—' : formatPercent(value);
    const { validation, approval, checks } = quote;
    // Once the CFO validated, what asked for it reads as validated (05/10/2026).
    const validated = validation.decision?.outcome === 'granted';
    const outcomeLabel = (outcome) => outcome === 'convalida' && validated ? 'Convalidato dal CFO' : CHECK_OUTCOME_LABELS[outcome];
    return {
        reference: referenceOf(quote),
        ribbon: 'Uso interno, non inviare',
        title: 'Preventivo',
        subtitle: `Stampato il ${italianTimestamp(printedAt)}${quote.referenceName ? ` · ${quote.referenceName}` : ''}`,
        facts: [
            { label: 'Cliente', value: quote.snapshot?.name ?? quote.clientName ?? '—' },
            { label: 'Commerciale', value: quote.owner?.name ?? '—' },
            {
                label: 'Offerta',
                value: quote.number
                    ? offerLabel(quote.number, quote.version)
                    : quote.versionOf
                        ? `${offerLabel(quote.versionOf.number, quote.versionOf.version)} in preparazione`
                        : 'nascerà con l’approvazione',
            },
            {
                label: 'Listini',
                value: `Attività: ${quote.priceLists.activity.name} · Passività: ${quote.priceLists.cost?.name ?? '—'}`,
            },
        ],
        groups: lineGroups(quote).map((group) => ({
            name: group.section?.name ?? null,
            lines: group.lines.map((line) => {
                const cost = costOf(line.id);
                return {
                    what: line.label,
                    costs: cost && cost.costs.length > 0
                        ? cost.costs.map((item) => item.description).join(' · ')
                        : null,
                    quantity: quantityText(line, labels),
                    revenue: formatMoney(line.sold),
                    cost: cost?.cost ? formatMoney(cost.cost) : '—',
                    margin: percent(cost?.margin?.percent),
                };
            }),
        })),
        transversal: Number(costs.transversal.total) > 0 ? formatMoney(costs.transversal.total) : null,
        total: {
            revenue: formatMoney(quote.totals.net),
            cost: costs.total ? formatMoney(costs.total.cost) : '—',
            margin: percent(costs.total?.percent),
        },
        note: 'Margine sul prezzo. Il costo del personale interno è il costo standard; il costo aziendale reale non compare.',
        conditions: [
            {
                label: 'Pagamento',
                value: quote.paymentTermsCode ? label(labels.paymentTerms, quote.paymentTermsCode) : '—',
            },
            { label: 'Fatturazione', value: quote.billingNotes || '—' },
            ...(quote.installments.length > 0
                ? [{ label: 'Piano di pagamento', value: installmentLines(quote, labels).join('\n') }]
                : []),
            {
                label: 'Durata',
                value: `${quote.startDate ? shortDate(quote.startDate) : '…'} – ${quote.endDate ? shortDate(quote.endDate) : '…'}`,
            },
            { label: 'Validità', value: `${quote.validityDays} giorni` },
            { label: 'Penali o clausole', value: quote.penalties ? 'Sì, non standard' : 'No' },
        ],
        checks: checks.checks.map((check) => ({
            label: check.label,
            detail: check.detail,
            outcome: check.outcome
                ? outcomeLabel(check.outcome)
                : check.key === 'amount'
                    ? checks.needsAdApproval
                        ? 'Approva l’AD'
                        : 'Approva il commerciale'
                    : checks.complexReasons.length > 0
                        ? 'Avviso'
                        : '—',
            bad: check.outcome === 'convalida' && !validated,
        })),
        outcome: {
            text: outcomeLabel(checks.outcome),
            bad: checks.outcome === 'convalida' && !validated,
        },
        approvals: [
            {
                label: 'Convalida del CFO',
                value: !checks.needsValidation
                    ? 'non serve'
                    : validation.decision
                        ? `${validation.decision.outcome === 'granted' ? 'convalidato' : 'rifiutata'}${validation.decision.by ? ` da ${validation.decision.by}` : ''} il ${italianTimestamp(validation.decision.at)}${validation.decision.note ? `: «${validation.decision.note}»` : ''}`
                        : validation.requested
                            ? `chiesta il ${italianTimestamp(validation.requested.at)}, in attesa`
                            : 'da chiedere',
            },
            {
                label: 'Approvazione',
                value: approval
                    ? `il ${italianTimestamp(approval.at)}${approval.by ? ` da ${approval.by}` : ''}`
                    : 'in attesa',
            },
        ],
        footer: `Documento interno di ${COMPANY.name}: contiene costi e margini riservati. Non va inviato al cliente.`,
    };
}
/** Body of `POST /api/quotes/:id/files/from-template`: the template to fill. */
export const prepareDocumentSchema = z.object({
    templateId: z.string().regex(/^[A-Za-z0-9!_-]{1,100}$/, { message: 'Scegli un modello' }),
});
/**
 * The data a Word template of the offer is filled with (strada B, decision of
 * 05/10/2026): the names are the {placeholders} of «Modello Offerta
 * K-Management». The texts to write by hand stay in the template.
 */
export function offerTemplateData(quote, labels) {
    const offer = offerDocumentModel(quote, labels);
    const term = (name) => offer.terms.find((item) => item.label === name)?.value ?? '';
    // The template has its own table of the instalments: «pagamento» stays the payment terms.
    const terms = quote.paymentTermsCode ? label(labels.paymentTerms, quote.paymentTermsCode) : '';
    const sezioni = lineGroups(quote).map((group, index) => {
        const righe = offerLines(group.lines).map((line, position) => {
            const net = Number(line.net);
            const listTotal = line.listPrice === null
                ? 0
                : Number(line.listPrice) * Number(line.quantity) * line.editions;
            const before = Math.max(Number(line.gross), listTotal);
            return {
                numero: `${index + 1}.${position + 1}`,
                voce: line.label,
                descrizione: line.description && !line.label.includes(line.description) ? line.description : null,
                importo: formatMoney(line.net),
                listino: before > net + 0.005 ? formatMoney(before) : null,
                ricorrenza: recurrenceOf(line, labels),
            };
        });
        const total = group.lines.reduce((sum, line) => sum + Number(line.net), 0);
        return {
            nome: group.section?.name ?? 'Attività',
            righe,
            totale: formatMoney(group.section?.net ?? total),
        };
    });
    return {
        numero: quote.number ?? '',
        versione: quote.version ?? 1,
        oggetto: quote.referenceName ?? '',
        cliente: offer.client.name,
        indirizzo_cliente: offer.client.address ?? '',
        piva_cliente: offer.client.vatNumber ?? '',
        data: longDate(quote.date),
        sezioni,
        tutte_le_righe: sezioni.flatMap((section) => section.righe),
        totale_offerta: offer.total,
        sconto: offer.discount ? formatMoney(quote.totals.documentDiscount) : null,
        pagamento: terms,
        fatturazione: quote.billingNotes ?? '',
        durata: term('Durata'),
        validita: term('Validità'),
        rate: quote.installments.map((item, index) => ({
            numero: index + 1,
            quando: item.description,
            percentuale: item.percent !== null ? `${Number(item.percent).toLocaleString('it-IT')}%` : null,
            importo: formatMoney(item.computed),
            incasso: item.termsCode ? label(labels.paymentTerms, item.termsCode) : terms,
            note: item.notes,
        })),
        /** The plan has instalments: the template shows its table only then. */
        ha_rate: quote.installments.length > 0,
        note_cliente: quote.clientNotes,
        commerciale: quote.owner?.name ?? '',
        email_commerciale: quote.owner?.email ?? '',
        pm: quote.projectManager?.name ?? '',
        email_pm: quote.projectManager?.email ?? '',
        amministrazione: quote.adminContact?.name ?? '',
        email_amministrazione: quote.adminContact?.email ?? '',
    };
}
/** «OFF-2026-0001 v2 - Offerta - Cliente.docx»: the name of the prepared document. */
export function preparedDocumentName(quote) {
    return `${quoteDocumentFileName(quote, 'offerta').replace(' - Offerta economica', ' - Offerta')}.docx`;
}
//# sourceMappingURL=quote-documents.js.map